import { useEffect, useState } from "react";
import { Download, X, Share, Smartphone, ChevronDown } from "lucide-react";

export default function InstallBanner({ onVisibleChange }) {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  // Shell (in App.js) used to find out whether this banner is showing by
  // polling the DOM for it every 500ms, forever, on every route — just so
  // it could add top padding under it. Report the real state up directly
  // instead.
  useEffect(() => { onVisibleChange?.(visible); }, [visible, onVisibleChange]);

  useEffect(() => {
    if (sessionStorage.getItem("gm_install_dismissed") === "1") return;
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) return;

    setIsIOS(/iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream);
    setVisible(true); // always show the "add to home screen" hint bar

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", () => setVisible(false));
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const dismiss = () => {
    setVisible(false);
    sessionStorage.setItem("gm_install_dismissed", "1");
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      await deferred.userChoice;
      setDeferred(null);
      setVisible(false);
    } else {
      setShowHelp((s) => !s);
    }
  };

  if (!visible) return null;

  return (
    <div data-testid="install-banner" className="fixed top-0 inset-x-0 z-[60] gm-fade-up">
      <div className="bg-gradient-to-r from-[#4e73df] to-[#3a5fd0] text-white px-4 py-2.5 flex items-center gap-3 shadow-lg">
        <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
          <Smartphone className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold leading-tight">Add Glitch Manager to your home screen</p>
          <p className="text-[11px] text-white/85 leading-tight">Use it like a native app — no app store needed</p>
        </div>
        <button
          data-testid="install-app-btn"
          onClick={install}
          className="bg-white text-[#4e73df] text-sm font-semibold px-3.5 py-1.5 rounded-full hover:bg-white/90 transition-colors shrink-0 flex items-center gap-1"
        >
          {deferred ? <Download className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          {deferred ? "Install" : "How?"}
        </button>
        <button data-testid="install-dismiss-btn" onClick={dismiss} className="p-1 hover:bg-white/15 rounded-full transition-colors shrink-0" aria-label="Dismiss">
          <X className="w-4 h-4" />
        </button>
      </div>
      {showHelp && !deferred && (
        <div className="bg-white text-slate-700 text-[13px] px-4 py-3 shadow-md border-b border-slate-200 gm-fade-up">
          {isIOS ? (
            <p className="flex items-center gap-1.5">On iPhone/iPad: tap <Share className="w-4 h-4 text-[#4e73df]" /> <b>Share</b>, then <b>“Add to Home Screen”</b>.</p>
          ) : (
            <p>On Android/Chrome: open the browser <b>⋮ menu</b> (top-right) → <b>“Add to Home screen”</b> / <b>“Install app”</b>. On desktop Chrome, use the install icon in the address bar.</p>
          )}
        </div>
      )}
    </div>
  );
}
